<template>
    <DForm
        class="contract-form"
        @submit.prevent="submitForm"
    >
        <FormBreadcrumbs
            class="mt-2"
            :breadcrumbs="breadcrumbs"
            :active-breadcrumb="active_breadcrumb"
            @breadcrumb-clicked="changeBreadcrumb"
        />

        <!-- Basic information -->
        <div v-show="active_breadcrumb === 'basic_information'">
            <ProductAddonsAndProductAddonGroup
                v-if="isAddon"
                :contract="contract"
                :form="form"
                :errors="errors"
                :customer="customer"
                :is-loading-contract-options="isLoadingContractOptions"
                :is-pre-live-contract="isContractPreLive"
                class="row-margin"
                @updateProductId="updateProductId"
            />
            <ProductAndProductGroup
                v-else
                :contract="contract"
                :form="form"
                :errors="errors"
                :customer="customer"
                :is-loading-contract-options="isLoadingContractOptions"
                :is-pre-live-contract="isContractPreLive"
                class="row-margin"
                @updateProductGroupId="updateProductGroupId"
                @updateProductId="updateProductId"
                @updateProductPlanId="updateProductPlanId"
                @productDeselected="productDeselected"
                @resetProductId="resetProductId"
            />

            <DAlert
                v-if="productUpAndDowngradeOnly === true"
                color="warning"
                class="row-margin"
            >
                {{ __('customers.contracts.up_and_downgrade_only.warning') }}
                <DFormRow
                    label=""
                    :error="errors.up_and_downgrade_only_confirmed"
                >
                    <DFormCheck
                        id="up_and_downgrade_only_confirmed"
                        v-model="form.up_and_downgrade_only_confirmed"
                        :label="__('customers.contracts.up_and_downgrade_only.checkbox_hint')"
                    />
                </DFormRow>
            </DAlert>

            <ProductPlan
                v-if="isContractPreLive === true"
                v-model="form.product_plan_id"
                :form="form"
                :errors="errors"
                :product-plans="productPlanOptions"
                :disabled="!form.product_id"
                @updateProductPlanId="updateProductPlanId"
            />

            <ExistingContract
                v-if="isContractPreLive === true && showExistingContract"
                v-model="form.existing_contract"
                :errors="errors"
                @existingContractChanged="handleExistingContractChanged"
            />

            <StartDate
                v-model="form.start_date"
                :errors="errors"
                :required="true"
                :disabled="!form.product_id || isContractPreLive === false"
                :show-hint="form.existing_contract === true"
                @update:modelValue="getDatesOverview"
                @valueChanged="handleProductPlanChange"
            />

            <LimitedRuntimeInMonth
                v-if="showLimitedRuntimeField"
                v-model="form.limited_runtime_in_month"
                :required="showLimitedRuntimeField"
                :errors="errors"
                @update:modelValue="updateLimitedRuntimeInMonth"
            />

            <LeadTime
                v-if="isInvoiceBeforeRenewalEnabled === false"
                v-model="form.lead_time"
                :errors="errors"
                :required="true"
                @valueChanged="getDatesOverview"
            />

            <BilledUntil
                v-if="isContractPreLive === false || form.existing_contract"
                v-model="form.billed_until_date"
                :errors="errors"
                :is-required="form.existing_contract"
                @update:modelValue="getDatesOverview"
            />

            <FulfillmentStrategy
                v-if="showFulfillmentStrategyIdField"
                v-model="form.fulfillment_strategy_id"
                :errors="errors"
                :fulfillment-strategies="fulfillmentStrategies"
                @update:modelValue="setFulfillmentStrategy"
            />

            <DFormRow
                :label="__('customers.contracts.billing_type.label')"
                :error="errors.billing_type"
                :hint="__('customers.contracts.billing_type.description')"
            >
                <DSelect
                    id="billing_type"
                    v-model="form.billing_type"
                    :options="billingTypes"
                    :is-valid="isFormFieldValid('billing_type')"
                    @update:modelValue="getDatesOverview"
                />
            </DFormRow>

            <DatesOverview
                v-if="datesOverview !== null"
                :dates-overview="datesOverview"
                :form="form"
                :contract="contract"
                :is-invoice-before-renewal-enabled="isInvoiceBeforeRenewalEnabled"
            />
        </div>

        <div v-show="active_breadcrumb === 'payment'">
            <PaymentMethod
                v-if="tenantSettings.enable_divergent_payment_methods === true"
                v-model="form.payment_method"
                :errors="errors"
                :available-payment-methods="availablePaymentMethods"
                @update:modelValue="updatePaymentMethod"
            />
            <Sepa
                v-if="shouldDisplaySepa"
                v-model:iban="form.iban"
                v-model:bic="form.bic"
                v-model:accountOwner="form.account_owner"
                :errors="errors"
                class="mb-4"
            />
            <PaymentTerm
                v-if="tenantSettings.enable_divergent_payment_methods === true"
                v-model="form.payment_term"
                :errors="errors"
                :payment-terms="paymentTerms"
                @update:modelValue="updatePaymentTerm"
            />
            <Installments
                v-if="isInstallmentsEnabled"
                v-model="form.installments"
                :errors="errors"
                :hint="calculatedInstallments"
                @update:modelValue="updateInstallments"
            />
            <PromptCashDiscountPercentage
                v-if="isPromptCashDiscountEnabled === true && isPaymentMethodSupportedForPromptDiscount === true && hidePromptCashDiscount === false"
                v-model="form.prompt_cash_discount_percentage"
                :errors="errors"
                @update:modelValue="updatePromptCashDiscountPercentage"
            />
            <PromptCashDiscountDays
                v-if="isPromptCashDiscountEnabled === true && isPaymentMethodSupportedForPromptDiscount === true && form.prompt_cash_discount_percentage > 0 && hidePromptCashDiscount === false"
                v-model="form.prompt_cash_discount_days"
                :errors="errors"
                @update:modelValue="updatePromptCashDiscountDays"
            />
        </div>

        <!-- Details -->
        <div v-if="form.product_id">
            <div v-show="active_breadcrumb === 'details'">
                <TermsAndConditions
                    v-model="form.terms_and_conditions_id"
                    :errors="errors"
                    :terms-and-conditions="termsAndConditions"
                    :required="form.id === undefined"
                />

                <ContractNumber
                    v-model="form.contract_number"
                    :errors="errors"
                />

                <Notes
                    v-model:notesInternal="form.notes_internal"
                    v-model:notesInvoice="form.notes_invoice"
                    :errors="errors"
                    class="row-margin"
                />

                <Parent
                    :is-addon="isAddon"
                    :contract="contract"
                    :form="form"
                    :customer="customer"
                    :errors="errors"
                    :product-group-id="productGroupId"
                    class="row-margin"
                    @updateParentValue="updateParentId"
                />

                <PreviousContract
                    v-model="form.previous_contract_ids"
                    :previous-contract-options="previousContractOptions"
                    :errors="errors"
                    @previousContractIdChanged="updatePreviousContractId"
                />

                <ParentRelationType
                    v-if="relationTypes.length > 0"
                    v-model:relationType="form.relation_type"
                    v-model:relationReference="form.relation_reference"
                    :options="relationTypeOptions"
                    :errors="errors"
                />

                <ResellerAccount
                    v-if="isContractPreLive === false && customer.is_reseller"
                    v-model="form.reseller_cloudpit_account_id"
                    :errors="errors"
                />

                <ResponsibleSalesAgent
                    v-model="form.responsible_sales_agent"
                    :errors="errors"
                />

                <ContractGroup
                    v-if="customer.split_invoices && (form.parent_contract_id === null || form.parent_contract_id === undefined || form.parent_contract_id === '')"
                    v-model="form.contract_group"
                    :errors="errors"
                    :existing-contract-groups="existingContractGroups"
                />

                <EasyMarketingId
                    v-model="form.easy_marketing_id"
                    :errors="errors"
                />

                <Orders
                    v-model="form.order_id"
                    :orders="orders"
                    :errors="errors"
                />

                <InvoiceText
                    v-if="contract.id !== undefined"
                    v-model="form.invoice_text"
                    :errors="errors"
                />
            </div>

            <!-- Pricing and runtimes -->
            <div v-if="(selectedProductPlan || contract.id !== undefined)">
                <div v-show="active_breadcrumb === 'pricing_and_runtimes'">
                    <div
                        class="d-flex row-margin"
                    >
                        <Prices
                            class="col-7 pl-0 me-3"
                            :form="form"
                            :contract="contract"
                            :errors="errors"
                            :billing-cycles="billingCycles"
                            :discount-types="discountTypes"
                            :is-selected-plan-one-time="isSelectedPlanOneTime"
                            :is-pre-live-contract="isContractPreLive"
                            :customer="customer"
                            @updatePrice="updatePrice"
                            @addPrice="addPrice"
                            @removePrice="removePrice"
                        />

                        <Runtimes
                            v-if="isSelectedPlanOneTime === false"
                            class="col-4 pl-0"
                            :form="form"
                            :contract="contract"
                            :errors="errors"
                            :contract-term-units="contractTermUnits"
                            :is-pre-live-contract="isContractPreLive"
                            @updateRuntime="updateRuntime"
                            @addRuntime="addRuntime"
                            @removeRuntime="removeRuntime"
                        />
                    </div>

                    <Prepaid
                        v-if="showPrepaidField"
                        v-model="form.prepaid"
                        :errors="errors"
                    />
                </div>
            </div>

            <!-- Fulfillment -->
            <div v-show="active_breadcrumb === 'technical_settings'">
                <div v-if="(form.existing_contract || isContractPreLive === false)">
                    <InstanceSettings
                        v-model:configValues="form.config_values"
                        v-model:externalId="form.external_id"
                        v-model:fulfillmentAccountId="form.fulfillment_account_id"
                        v-model:dnsProviderId="form.dns_provider_id"
                        :form="form"
                        :contract="contract"
                        :errors="errors"
                        :fulfillment-accounts="fulfillmentAccounts"
                        :is-loading-fulfillment-accounts="isLoadingFulfillmentAccounts"
                        :fulfillment-module="fulfillmentModule"
                        :external-id-mapping="externalIdMapping"
                        :is-pre-live-contract="isContractPreLive"
                        :dns-providers="dnsProviders"
                        class="row-margin"
                    />

                    <hr>
                </div>

                <ConfigOptions
                    v-if="configValuesNotEmpty"
                    v-model:configValues="form.config_values"
                    :contract="contract"
                    :form="form"
                    :errors="errors"
                    :is-pre-live-contract="isContractPreLive"
                    :fulfillment-module="selectedFulfillmentModule"
                    :sorting-type="configValuesSortingType"
                />

                <DAlert
                    v-if="selectedFulfillmentModule === null"
                    color="warning"
                >
                    {{ __('customers.contracts.no_fulfillment_module') }}
                </DAlert>

                <div v-if="ffPackageContracts.length > 0">
                    <hr>
                    <FulfillmentPackage
                        v-model:ffPackageContracts="ffPackageContracts"
                        :errors="errors"
                        :start-date="form.start_date"
                    />
                </div>
            </div>
        </div>

        <DFormControls
            v-model="form.activity_log_comment"
            :disabled="unableToLoadOptions === true"
            :cancel-route="cancelRoute"
            :dirty="form.isDirty"
            :show-change-reason-input="true"
            :submit-message="contract.id !== undefined && contract.status !== 'draft' ? __('messages.save') : __('customers.contracts.save_draft_contract')"
            :show-incident-list="contract.id ? true : false"
            :customer-id="contract.id ? customer.id : 0"
            @setIncident="setIncident"
            @submit="submitForm"
        >
            <template #additional_button_before_submit>
                <DFab
                    v-if="backButtonAvailable"
                    id="btn-back"
                    icon="arrow-left"
                    :label="__('messages.back')"
                    @click="changeToPreviousBreadcrumb"
                />
                <DFab
                    v-if="nextButtonAvailable"
                    id="btn-next"
                    icon="arrow-right"
                    icon-position="after"
                    :label="__('messages.next')"
                    @click="changeToNextBreadcrumb"
                />
            </template>
            <template #additional_button>
                <DFab
                    v-if="(contract.id === undefined || contract.status === 'draft') && parentContractStatus !== 'draft'"
                    id="btn-release"
                    :icon="'content-save'"
                    :disabled="unableToLoadOptions === true"
                    :label="__('customers.contracts.release.name')"
                    @click="submitReleaseContractForm"
                />
            </template>
        </DFormControls>
        <DModal
            :show="unableToLoadOptions === true"
            :title="__('contracts.modals.options_not_loaded.title')"
            :message="__('contracts.modals.options_not_loaded.description')"
            is-error
        />
    </DForm>
</template>

<script>
import ConfigOptions from '~@/Components/Customers/Contracts/FormFields/ConfigOptions.vue'
import InstanceSettings from '~@/Components/Customers/Contracts/FormFields/InstanceSettings.vue'
import ResellerAccount from '~@/Components/Customers/Contracts/FormFields/ResellerAccountId.vue'
import Parent from '~@/Components/Customers/Contracts/FormFields/Parent.vue'
import ParentRelationType from '~@/Components/Customers/Contracts/FormFields/ParentRelationType.vue'
import Notes from '~@/Components/Customers/Contracts/FormFields/Notes.vue'
import Orders from '~@/Components/Customers/Contracts/FormFields/Orders.vue'
import ContractNumber from '~@/Components/Customers/Contracts/FormFields/ContractNumber.vue'
import TermsAndConditions from '~@/Components/Customers/Contracts/FormFields/TermsAndConditions.vue'
import BilledUntil from '~@/Components/Customers/Contracts/FormFields/BilledUntil.vue'
import StartDate from '~@/Components/Customers/Contracts/FormFields/StartDate.vue'
import LeadTime from '~@/Components/Customers/Contracts/FormFields/LeadTime.vue'
import DatesOverview from '~@/Components/Customers/Contracts/FormFields/DatesOverview.vue'
import ExistingContract from '~@/Components/Customers/Contracts/FormFields/ExistingContract.vue'
import ProductPlan from '~@/Components/Customers/Contracts/FormFields/ProductPlan.vue'
import ProductAndProductGroup from '~@/Components/Customers/Contracts/FormFields/ProductAndProductGroup.vue'
import ProductAddonsAndProductAddonGroup from '~@/Components/Customers/Contracts/FormFields/ProductAddonsAndProductAddonGroup.vue'
import PreviousContract from '~@/Components/Customers/Contracts/FormFields/PreviousContract.vue'

import Prices from '~@/Components/Customers/Contracts/FormFields/Prices.vue'
import Runtimes from '~@/Components/Customers/Contracts/FormFields/Runtimes.vue'
import priceFormat from '~@/Mixins/priceFormat'
import ResponsibleSalesAgent from './FormFields/ResponsibleSalesAgent'
import { toRaw } from 'vue'
import ContractGroup from '~@/Components/Customers/Contracts/FormFields/ContractGroup.vue'
import LimitedRuntimeInMonth from '~@/Components/Customers/Contracts/FormFields/LimitedRuntimeInMonth.vue'
import FormBreadcrumbs from './FormBreadcrumbs.vue'
import FulfillmentPackage from '~@/Components/Customers/Contracts/FormFields/FulfillmentPackage'
import PromptCashDiscountPercentage from '~@/Components/Customers/Contracts/FormFields/PromptCashDiscountPercentage'
import PromptCashDiscountDays from '~@/Components/Customers/Contracts/FormFields/PromptCashDiscountDays'
import PaymentTerm from '~@/Components/Customers/Contracts/FormFields/PaymentTerm'
import PaymentMethod from '~@/Components/Customers/Contracts/FormFields/PaymentMethod'
import Sepa from '~@/Components/Customers/Contracts/FormFields/Sepa'
import EasyMarketingId from './FormFields/EasyMarketingId.vue'
import Installments from '~@/Components/Customers/Contracts/FormFields/Installments'
import { __ } from '~@/Composables/translation'
import FulfillmentStrategy from './FormFields/FulfillmentStrategy.vue'
import Prepaid from './FormFields/Prepaid.vue'
import InvoiceText from '~@/Components/Customers/Contracts/FormFields/InvoiceText.vue'

const getDateFormatted = (date) => {
    date = date ?? new Date()

    let day = date.getDate()
    let month = date.getMonth() + 1
    const year = date.getFullYear()

    if (month < 10) month = '0' + month
    if (day < 10) day = '0' + day

    return year + '-' + month + '-' + day
}

const getDateAfterMonths = (date, months) => {
    const newDate = new Date(date)
    const currentDate = newDate.getDate()
    newDate.setMonth(newDate.getMonth() + Number(months))
    if (newDate.getDate() !== currentDate) {
        newDate.setDate(0)
    }

    return getDateFormatted(newDate)
}

const supportedPaymentMethods = ['sepa', 'sepa-b2b', 'banktransfer']

const fieldsBySection = {
    basic_information: [
        'product_id',
        'product_plan_id',
        'existing_contract',
        'start_date',
        'limited_runtime_in_month',
        'limited_runtime_in_month_is_required',
        'lead_time',
        'billed_until_date',
        'up_and_downgrade_only_confirmed'
    ],
    payment: [
        'payment_method',
        'payment_term',
        'iban',
        'bic',
        'account_owner',
        'installments',
        'prompt_cash_discount_percentage',
        'prompt_cash_discount_days'
    ],
    details: [
        'terms_and_conditions_id',
        'contract_number',
        'notes_invoice',
        'notes_internal',
        'parent_contract_id',
        'reseller_cloudpit_account_id',
        'responsible_sales_agent',
        'contract_group',
        'relation_type',
        'relation_reference',
        'invoice_text'
    ],
    pricing_and_runtimes: [
        'prices',
        'runtimes'
    ],
    technical_settings: [
        'config_value',
        'ff_package_contracts',
        'external_id',
        'fulfillment_account_id'
    ]
}

export default {
    name: 'ContractForm',
    components: {
        InvoiceText,
        Orders,
        FulfillmentStrategy,
        Prepaid,
        EasyMarketingId,
        FulfillmentPackage,
        FormBreadcrumbs,
        LimitedRuntimeInMonth,
        ContractGroup,
        ResponsibleSalesAgent,
        Runtimes,
        Prices,
        ProductAndProductGroup,
        ProductPlan,
        ExistingContract,
        StartDate,
        LeadTime,
        DatesOverview,
        BilledUntil,
        TermsAndConditions,
        ContractNumber,
        Notes,
        Parent,
        ParentRelationType,
        ResellerAccount,
        InstanceSettings,
        ConfigOptions,
        ProductAddonsAndProductAddonGroup,
        PromptCashDiscountPercentage,
        PromptCashDiscountDays,
        PaymentTerm,
        PaymentMethod,
        Sepa,
        Installments,
        PreviousContract
    },
    mixins: [
        priceFormat
    ],
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        contract: {
            type: Object,
            default: () => { return {} }
        },
        billingCycles: {
            type: Array,
            default: () => { return [] }
        },
        contractTermUnits: {
            type: Array,
            default: () => { return [] }
        },
        discountTypes: {
            type: Array,
            default: () => { return [] }
        },
        termsAndConditions: {
            type: Array,
            default: () => { return [] }
        },
        activeTermAndCondition: {
            type: Object,
            default: () => { return {} }
        },
        externalIdMapping: {
            type: Object,
            default: () => { return {} }
        },
        existingContractGroups: {
            type: Array,
            default: () => { return [] }
        },
        fulfillmentModule: {
            type: String,
            default: null
        },
        isAddon: {
            type: Boolean,
            default: () => false
        },
        hasSepaMandate: {
            type: Boolean,
            default: () => false
        },
        paymentMethods: {
            type: Array,
            default: () => { return [] }
        },
        paymentTerms: {
            type: Array,
            default: () => { return [] }
        },
        isInvoiceBeforeRenewalEnabled: {
            type: Boolean,
            default: () => false
        },
        supportedRelationTypes: {
            type: Array,
            default: () => []
        },
        fulfillmentStrategies: {
            type: Array,
            default: () => []
        },
        billingTypes: {
            type: Array,
            default: () => []
        },
        orders: {
            type: Array,
            default: () => { return [] }
        },
        previousContractOptions: {
            type: Array,
            default: () => { return [] }
        },
        dnsProviders: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['submit'],
    data () {
        return {
            form: this.$inertia.form({
                // Basic information
                id: this.contract?.id,
                product_id: this.contract?.product_id,
                product_plan_id: this.contract?.product_plan_id,
                existing_contract: this.contract.is_pre_live === true && this.contract.fulfillment_account_id !== null,
                start_date: this.contract.id !== undefined ? this.contract.start_date : getDateFormatted(),
                limited_runtime_in_month: this.contract.limited_runtime_in_month,
                limited_runtime_in_month_is_required: true,
                lead_time: this.contract.id !== undefined ? this.contract.lead_time : 0,
                fulfillment_strategy_id: this.contract.id !== undefined ? this.contract.fulfillment_strategy_id : null,
                billed_until_date: this.contract.billed_until_date,

                // Details
                terms_and_conditions_id: this.contract.terms_and_conditions_id ?? this.activeTermAndCondition?.id ?? null,
                contract_number: this.contract.contract_number,
                notes_invoice: this.contract.notes_invoice,
                notes_internal: this.contract.notes_internal,
                parent_contract_id: this.contract.contract_id ?? this.contract.parent_contract_id,
                reseller_cloudpit_account_id: this.contract.reseller_cloudpit_account_id,
                responsible_sales_agent: this.contract.responsible_sales_agent,
                contract_group: this.contract.contract_group,
                easy_marketing_id: this.contract.easy_marketing_id,
                relation_type: this.contract.relation_type,
                relation_reference: this.contract.relation_reference,
                order_id: this.contract.order_id ?? null,
                invoice_text: this.contract.invoice_text ?? null,

                // Pricing and runtimes
                prices: this.contract?.prices
                    ? this.contract?.prices.map((price) => {
                        return {
                            ...price,
                            price: this.numberFormatForInput(price.price),
                            discount_value: price.discount_value ? this.numberFormatForInput(price.discount_value) : null,
                            third_party_cost: this.numberFormatForInput(price.third_party_cost),
                            setup_fee: this.numberFormatForInput(price.setup_fee)
                        }
                    })
                    : [],
                runtimes: this.contract?.runtimes ?? [],
                prepaid: this.contract?.order?.prepaid_invoice ?? false,

                // Fulfillment
                external_id: this.contract.external_id ?? null,
                fulfillment_account_id: this.contract.fulfillment_account_id,
                dns_provider_id: this.contract.domain?.dns_provider?.id ?? null,
                config_values: this.contract?.options ?? {},
                ff_package_contracts: [],

                // Other
                release_contract: false,
                up_and_downgrade_only_confirmed: false,
                activity_log_comment: '',
                billing_type: this.contract?.billing_type ?? null,
                payment_method: this.contract?.payment_method,
                payment_term: this.contract?.payment_term ??
                    (
                        this.$page.props.tenantSettings?.enable_divergent_payment_methods === true
                            ? (this.paymentTerms.find(term => term.is_default === true)?.value ?? (this.paymentTerms[0].value ?? null))
                            : null
                    ),
                iban: this.contract?.iban ?? null,
                bic: this.contract?.bic ?? null,
                account_owner: this.contract?.account_owner ?? null,
                has_sepa_mandate: this.hasSepaMandate ?? false,
                prompt_cash_discount_percentage: this.contract?.prompt_cash_discount_percentage ?? null,
                prompt_cash_discount_days: this.contract?.prompt_cash_discount_days ?? null,
                installments: this.contract?.installments ?? null,
                previous_contract_ids: this.contract?.previous_contract_ids ?? [],
                incident_id: null,
                product_changed: false
            }),
            configValuesSortingType: this.contract.options_sorting_type,
            productPlans: this.contract.available_product_plans,
            isLoadingContractOptions: false,
            isLoadingFulfillmentAccounts: false,
            isSelectedPlanOneTime: false,
            productUpAndDowngradeOnly: false,
            selectedFulfillmentModule: this.contract?.fulfillment_module ?? null,
            fulfillmentAccounts: [],
            selectedProductPlan: null,
            productGroupId: null,
            showExistingContract: false,
            datesOverview: null,
            unableToLoadOptions: false,
            productPlanIsLimited: this.contract.product_plan_is_limited,
            active_breadcrumb: 'basic_information',
            breadcrumbs: [
                {
                    key: 'basic_information',
                    text: this.__('customers.contracts.basic_information'),
                    hasError: false,
                    isShown: true
                },
                ...(this.showPaymentBreadcrumbs()
                    ? [
                        {
                            key: 'payment',
                            text: this.__('customers.contracts.payment'),
                            hasError: false,
                            isShown: this.showPaymentBreadcrumbs()
                        }
                    ]
                    : []
                ),
                {
                    key: 'details',
                    text: this.__('customers.contracts.details'),
                    hasError: false,
                    isShown: true
                },
                {
                    key: 'pricing_and_runtimes',
                    text: this.__('customers.contracts.pricing_and_runtimes'),
                    hasError: false,
                    isShown: true
                },
                {
                    key: 'technical_settings',
                    text: this.__('customers.contracts.config'),
                    hasError: false,
                    isShown: true
                }
            ],
            ffPackageContracts: this.contract?.ff_package_contracts ?? [],
            availablePaymentMethods: this.paymentMethods ?? null,
            relationTypes: this.supportedRelationTypes,
            parentContractStatus: this.contract?.parent?.status ?? null
        }
    },
    computed: {
        // Basic information
        /**
         * @returns Array
         */
        productPlanOptions () {
            return this.productPlans
        },

        // Other
        cancelRoute () {
            return this.form.id
                ? this.$route('customers.customers.contracts.show', { customer: this.customer.id, contract: this.form.id })
                : this.$route('customers.customers.contracts.index', { customer: this.customer.id })
        },
        isContractPreLive () {
            return this.contract.id === undefined || this.contract.is_pre_live === true
        },
        configValuesNotEmpty () {
            return Object.values(this.form.config_values).length > 0
        },
        showFulfillmentStrategyIdField () {
            if (this.contract.is_live === true) {
                return false
            }
            if (this.form.product_id === null || this.form.product_id === undefined) {
                return false
            }

            return true
        },
        showPrepaidField () {
            if (
                this.contract.status === 'draft' &&
                this.form.installments === null
            ) {
                return true
            }
            if (this.contract.id !== undefined) {
                return false
            }
            if (this.form.existing_contract) {
                return false
            }
            if (this.form.fulfillment_strategy_id === 'after_payment') {
                return false
            }
            if (this.form.order_id) {
                return false
            }
            if (this.form.installments !== null) {
                return false
            }

            return true
        },
        showLimitedRuntimeField () {
            if (this.contract.id === undefined) {
                return this.productPlanIsLimited
            } else if (this.contract.is_pre_live === true && this.productPlanIsLimited) {
                return true
            }

            return false
        },
        backButtonAvailable () {
            return this.active_breadcrumb !== 'basic_information'
        },
        nextButtonAvailable () {
            return this.active_breadcrumb !== 'technical_settings'
        },
        tenantSettings () {
            return this.$page.props.tenantSettings
        },
        shouldDisplaySepa () {
            return (this.form.payment_method === 'sepa' || this.form.payment_method === 'sepa-b2b') && this.hasSepaMandate === false
        },
        isPaymentMethodSupportedForPromptDiscount () {
            if (this.tenantSettings?.enable_divergent_payment_methods === true) {
                return supportedPaymentMethods.includes(this.form.payment_method)
            }

            return supportedPaymentMethods.includes(this.customer.payment_method)
        },
        isPromptCashDiscountEnabled () {
            return this.tenantSettings?.enable_prompt_cash_discount === true
        },
        isInstallmentsEnabled () {
            return this.tenantSettings?.supports_installments === true &&
                (this.form.prices[0]?.billing_cycle > 1 || this.isSelectedPlanOneTime) &&
                supportedPaymentMethods.includes(this.currentPaymentMethod) &&
                this.form.fulfillment_strategy_id !== 'after_payment'
        },
        calculatedInstallments () {
            if (this.form.prices === null) {
                return null
            }

            if (this.form.installments === null || this.form.installments === '') {
                return __('customers.contracts.no_installments')
            }

            const currentPhasePrice = this.filterCurrentPhasePrice()

            const amount = this.internationalToNormalFormat(currentPhasePrice.price) / this.form.installments

            const maxInstallments = this.isSelectedPlanOneTime ? 12 : currentPhasePrice?.billing_cycle

            if (this.form.installments < 2 || this.form.installments > maxInstallments) {
                return __('customers.contracts.installments_amount_error', { maxInstallments: maxInstallments })
            }

            return __('customers.contracts.installments_amount', { amount: amount.toFixed(2) })
        },
        currentPaymentMethod () {
            if (this.tenantSettings.enable_divergent_payment_methods === true) {
                return this.form.payment_method
            }

            return this.customer.payment_method
        },
        hidePromptCashDiscount () {
            return this.form.installments !== null
        },
        relationTypeOptions () {
            const options = [{
                label: '',
                value: ''
            }]
            for (let i = 0; i < this.relationTypes.length; ++i) {
                options.push({
                    label: this.relationTypes[i],
                    value: this.relationTypes[i]
                })
            }
            return options
        }
    },
    watch: {
        'form.config_values': {
            handler () {
                this.setOptionVisibility()
            },
            deep: true,
            immediate: true
        },
        errors: {
            handler () {
                this.setBreadCrumbsErrors()
            },
            deep: true,
            immediate: true
        }
    },
    created () {
        if (this.contract.id) {
            if (this.contract.available_product_plans.length === 1) {
                this.form.product_plan_id = this.contract.available_product_plans[0].value
            }

            if (this.contract.active_contract_phase) {
                this.isSelectedPlanOneTime = this.contract.active_contract_phase.billing_cycle === null
            }

            this.getDatesOverview()

            this.updateFulfillmentAccounts()

            this.setOptionVisibility()

            const contractPrices = []

            contractPrices.push({
                start_date: this.contract.contract_phases[0].start_date,
                price: this.numberFormatForInput(this.contract.contract_phases[0].price),
                third_party_cost: this.numberFormatForInput(this.contract.contract_phases[0].third_party_cost),
                billing_cycle: this.contract.contract_phases[0].billing_cycle,
                discount_name: this.contract.contract_phases[0].discount_name,
                discount_value: this.contract.contract_phases[0].discount_value
                    ? this.numberFormatForInput(this.contract.contract_phases[0].discount_value)
                    : null,
                discount_type: this.contract.contract_phases[0].discount_type,
                setup_fee: this.numberFormatForInput(this.contract.contract_phases[0].setup_fee),
                phase_text: this.contract.contract_phases[0].phase_text
            })

            const contractRuntimes = []

            contractRuntimes.push({
                start_date: this.contract.contract_phases[0].start_date,
                contract_term: this.contract.contract_phases[0].contract_term,
                contract_term_unit: this.contract.contract_phases[0].contract_term_unit,
                notice_period: this.contract.contract_phases[0].notice_period,
                notice_period_unit: this.contract.contract_phases[0].notice_period_unit
            })

            for (const contractPhaseIndex in this.contract.contract_phases) {
                if (contractPhaseIndex === 0) {
                    continue
                }

                if (this.pricingIsDifferent(contractPrices[contractPrices.length - 1], this.contract.contract_phases[contractPhaseIndex])) {
                    contractPrices.push({
                        start_date: this.contract.contract_phases[contractPhaseIndex].start_date,
                        price: this.numberFormatForInput(this.contract.contract_phases[contractPhaseIndex].price),
                        third_party_cost: this.numberFormatForInput(this.contract.contract_phases[contractPhaseIndex].third_party_cost),
                        billing_cycle: this.contract.contract_phases[contractPhaseIndex].billing_cycle,
                        discount_name: this.contract.contract_phases[contractPhaseIndex].discount_name,
                        discount_value: this.contract.contract_phases[contractPhaseIndex].discount_value
                            ? this.numberFormatForInput(this.contract.contract_phases[contractPhaseIndex].discount_value)
                            : null,
                        discount_type: this.contract.contract_phases[contractPhaseIndex].discount_type,
                        setup_fee: this.numberFormatForInput(this.contract.contract_phases[contractPhaseIndex].setup_fee),
                        phase_text: this.contract.contract_phases[contractPhaseIndex].phase_text
                    })
                }

                if (this.runtimeIsDifferent(contractRuntimes[contractRuntimes.length - 1], this.contract.contract_phases[contractPhaseIndex])) {
                    contractRuntimes.push({
                        start_date: this.contract.contract_phases[contractPhaseIndex].start_date,
                        contract_term: this.contract.contract_phases[contractPhaseIndex].contract_term,
                        contract_term_unit: this.contract.contract_phases[contractPhaseIndex].contract_term_unit,
                        notice_period: this.contract.contract_phases[contractPhaseIndex].notice_period,
                        notice_period_unit: this.contract.contract_phases[contractPhaseIndex].notice_period_unit
                    })
                }
            }

            this.form.prices = contractPrices
            this.form.runtimes = contractRuntimes
            this.getSupportedRelationTypes()
        }
    },
    methods: {
        // Basic information
        updateProductGroupId (value) {
            this.showExistingContract = false
            this.form.existing_contract = false
            this.productGroupId = value
        },
        updateProductId (value) {
            const today = new Date()
            const startDate = today.setDate(today.getDate() + value.lead_time)

            this.form.product_id = value.value
            this.form.lead_time = value.lead_time
            this.form.start_date = new Date(startDate).toISOString().substring(0, 10)
            this.productPlans = value.plans
            this.form.prices = []
            this.form.runtimes = []
            this.selectedFulfillmentModule = value.fulfillment_module
            this.showExistingContract = true
            this.form.fulfillment_strategy_id = value.fulfillment_strategy
            this.form.billing_type = value.billing_type ?? null
            this.productUpAndDowngradeOnly = value.up_and_downgrade_only === true
            this.form.up_and_downgrade_only_confirmed = false

            // We need to reset config values in order to avoid validation error and to store new default values for next product (look comment on OCN-8840)
            this.form.config_values = {}
            this.form.product_changed = true

            this.getDatesOverview()

            this.updateFulfillmentAccounts()

            this.updateContractConfiguration()

            this.getFFPackage()
        },
        async updateFulfillmentAccounts () {
            this.fulfillmentAccounts = [{ label: '', value: '' }]

            if (this.form.product_id) {
                this.isLoadingFulfillmentAccounts = true

                const response = await window.axios.get(this.$route('products.dropdown.accounts', {
                    product: this.form.product_id
                }))

                for (const option of Object.values(response.data)) {
                    this.fulfillmentAccounts.push(option)
                }

                this.isLoadingFulfillmentAccounts = false
            }
        },
        async updateContractConfiguration () {
            const newConfigOptions = {}

            if (this.form.product_id) {
                this.unableToLoadOptions = false
                this.isLoadingContractOptions = true

                const response = await window.axios.get(this.$route('customers.customers.contracts.options', {
                    customer: this.customer.id,
                    product: this.form.product_id
                }))

                if (response.data.options === null) {
                    this.unableToLoadOptions = true
                } else {
                    for (const option of response.data.options) {
                        newConfigOptions[option.name] = { ...option, visible: true }
                    }

                    this.configValuesSortingType = response.data.sorting_type
                }

                this.isLoadingContractOptions = false
            }

            this.form.config_values = newConfigOptions
        },
        setOptionVisibility () {
            for (const option of Object.values(this.form.config_values)) {
                this.form.config_values[option.name].visible = this.optionVisible(option)
            }
        },
        optionVisible (option) {
            const dependencies = option.dependencyVisibilityOptions

            for (const configOption of Object.values(this.form.config_values)) {
                if (configOption.name in dependencies && this.form.config_values[configOption.name].value !== dependencies[configOption.name]) {
                    return false
                }
            }

            return true
        },
        updateProductPlanId (value) {
            this.form.product_plan_id = value
            this.isSelectedPlanOneTime = false

            let selectedPlan = null

            if (value !== null) {
                toRaw(this.productPlanOptions).forEach((planItemValue) => {
                    if (Number(planItemValue.value) === Number(value)) {
                        this.selectedProductPlan = value
                        selectedPlan = planItemValue

                        if (planItemValue.one_time === true) {
                            this.isSelectedPlanOneTime = true

                            this.form.start_date = getDateFormatted()
                        }
                        if (planItemValue.runtime === 'limited') {
                            this.productPlanIsLimited = true
                            if (!this.form.limited_runtime_in_month) {
                                this.updateLimitedRuntimeInMonth(planItemValue.runtime_in_month)
                            } else {
                                this.updateLimitedRuntimeInMonth(this.contract.limited_runtime_in_month)
                            }
                        } else {
                            this.productPlanIsLimited = false
                            this.updateLimitedRuntimeInMonth(null)
                        }
                    }
                })
            }

            selectedPlan && this.handleProductPlanChange(selectedPlan)
        },
        productDeselected () {
            this.form.product_id = null
            this.form.prices = []
            this.form.runtimes = []
            this.form.config_values = {}
            this.form.fulfillment_after_payment = false
            this.form.prepaid = false
            this.productPlans = []
            this.datesOverview = null
            this.productUpAndDowngradeOnly = false
            this.form.up_and_downgrade_only_confirmed = false
        },
        resetProductId () {
            this.form.product_id = this.contract.product_id
            this.form.product_plan_id = this.contract.product_plan_id
            this.form.prices = []
            this.form.runtimes = []
            this.productUpAndDowngradeOnly = false
            this.form.up_and_downgrade_only_confirmed = false
        },
        handleExistingContractChanged (value) {
            if (value === true) {
                this.form.fulfillment_account_id = this.fulfillmentAccounts.filter((account) => account.value)[0]?.value

                return
            }

            this.form.fulfillment_account_id = null
            this.form.billed_until_date = null
            this.getDatesOverview()
        },
        async getDatesOverview () {
            const response = await window.axios.post(this.$route('customers.customers.contracts.dates-overview', {
                customer: this.customer.id
            }), {
                lead_time: this.form.lead_time,
                start_date: this.form.start_date,
                product_id: this.form.product_id,
                billed_until_date: this.form.billed_until_date,
                fulfillment_strategy_id: this.form.fulfillment_strategy_id,
                billing_type: this.form.billing_type
            })

            this.datesOverview = response.data
        },
        setFulfillmentStrategy (value) {
            if (value === 'after_payment') {
                this.form.prepaid = false
                this.form.installments = null
            }
            this.getDatesOverview()
        },

        // Details
        updateParentId (value) {
            this.form.parent_contract_id = value.value
            this.form.relation_type = null
            this.form.relation_reference = null
            this.parentContractStatus = value.contract_status
            this.getSupportedRelationTypes()
        },

        // Pricing and runtimes
        handleProductPlanChange (value) {
            if (!value) {
                toRaw(this.productPlanOptions).forEach((planItemValue) => {
                    if (Number(planItemValue.value) === Number(this.form.product_plan_id)) {
                        value = planItemValue
                    }
                })
            }

            const newPrices = []
            const newRuntimes = []
            for (const price of value.prices) {
                if (newPrices.length === 0) {
                    newPrices.push({
                        start_date: this.form.start_date,
                        price: this.numberFormatForInput(price.price),
                        third_party_cost: this.numberFormatForInput(price.third_party_cost),
                        billing_cycle: price.billing_cycle,
                        discount_name: price.discount_name,
                        discount_value: price.discount_value ? this.numberFormatForInput(price.discount_value) : null,
                        discount_type: price.discount_type,
                        setup_fee: this.numberFormatForInput(price.setup_fee),
                        phase_text: this.getPhaseTextFromProductPlan(this.productPlans, this.selectedProductPlan)
                    })
                } else if (this.pricingIsDifferent(newPrices[newPrices.length - 1], price)) {
                    newPrices.push({
                        start_date: getDateAfterMonths(
                            newPrices[newPrices.length - 1].start_date,
                            price.valid_from_month - 1
                        ),
                        price: this.numberFormatForInput(price.price),
                        third_party_cost: this.numberFormatForInput(price.third_party_cost),
                        billing_cycle: price.billing_cycle,
                        discount_name: price.discount_name,
                        discount_value: price.discount_value ? this.numberFormatForInput(price.discount_value) : null,
                        discount_type: price.discount_type,
                        setup_fee: this.numberFormatForInput(price.setup_fee),
                        phase_text: this.getPhaseTextFromProductPlan(this.productPlans, this.selectedProductPlan)
                    })
                }

                if (newRuntimes.length === 0) {
                    newRuntimes.push({
                        start_date: this.form.start_date,
                        contract_term: price.contract_term,
                        contract_term_unit: price.contract_term_unit,
                        notice_period: price.notice_period,
                        notice_period_unit: price.notice_period_unit
                    })
                } else if (this.runtimeIsDifferent(newRuntimes[newRuntimes.length - 1], price)) {
                    newRuntimes.push({
                        start_date: getDateAfterMonths(
                            newRuntimes[newRuntimes.length - 1].start_date,
                            price.valid_from_month - 1
                        ),
                        contract_term: price.contract_term,
                        contract_term_unit: price.contract_term_unit,
                        notice_period: price.notice_period,
                        notice_period_unit: price.notice_period_unit
                    })
                }
            }

            this.form.prices = newPrices
            this.form.runtimes = newRuntimes
        },
        pricingIsDifferent (price1, price2) {
            return this.internationalToNormalFormat(price1.price) !== this.internationalToNormalFormat(price2.price) ||
                price1.billing_cycle !== price2.billing_cycle ||
                price1.discount_name !== price2.discount_name ||
                this.internationalToNormalFormat(price1.discount_value) !== this.internationalToNormalFormat(price2.discount_value) ||
                price1.discount_type !== price2.discount_type
        },
        runtimeIsDifferent (price1, price2) {
            return price1.contract_term !== price2.contract_term ||
                price1.contract_term_unit !== price2.contract_term_unit ||
                price1.notice_period !== price2.notice_period ||
                price1.notice_period_unit !== price2.notice_period_unit
        },
        updatePrice (index, price) {
            const newPrices = [...this.form.prices]
            newPrices[index] = price

            this.form.prices = this.sortByStartDate(newPrices)
        },
        addPrice (price) {
            const newPrices = [...this.form.prices]
            newPrices.push(price)

            this.form.prices = this.sortByStartDate(newPrices)
        },
        removePrice (index) {
            const newPrices = [...this.form.prices].filter((value, priceIndex) => index !== priceIndex)

            this.form.prices = this.sortByStartDate(newPrices)
        },
        updateRuntime (index, runtime) {
            const newRuntimes = [...this.form.runtimes]
            newRuntimes[index] = runtime

            this.form.runtimes = this.sortByStartDate(newRuntimes)
        },
        addRuntime (runtime) {
            const newRuntimes = [...this.form.runtimes]
            newRuntimes.push(runtime)

            this.form.runtimes = this.sortByStartDate(newRuntimes)
        },
        removeRuntime (index) {
            const newRuntimes = [...this.form.runtimes].filter((value, runtimeIndex) => index !== runtimeIndex)

            this.form.runtimes = this.sortByStartDate(newRuntimes)
        },
        sortByStartDate (entries) {
            return entries.sort((a, b) => {
                return new Date(a.start_date) - new Date(b.start_date)
            })
        },

        // Fulfillment

        // Other
        submitForm () {
            this.form.release_contract = false
            this.form.prices = this.sortByStartDate(this.form.prices)
            this.form.runtimes = this.sortByStartDate(this.form.runtimes)
            this.form.ff_package_contracts = this.flattenContracts(this.ffPackageContracts)

            this.$emit('submit', this.form)
        },
        submitReleaseContractForm () {
            this.form.release_contract = true
            this.form.ff_package_contracts = this.flattenContracts(this.ffPackageContracts)

            this.$emit('submit', this.form)
        },
        updateLimitedRuntimeInMonth (value) {
            this.form.limited_runtime_in_month = value
        },
        setBreadCrumbsErrors () {
            const breadCrumbs = []

            for (const breadcrumb of this.breadcrumbs) {
                breadCrumbs.push({
                    ...breadcrumb,
                    hasError: this.breadcrumbHasErrors(breadcrumb)
                })
            }

            this.breadcrumbs = breadCrumbs
        },
        breadcrumbHasErrors (breadcrumb) {
            for (const filedWithError of Object.keys(this.errors)) {
                for (const fieldInBreadcrumbSection of fieldsBySection[breadcrumb.key]) {
                    if (filedWithError.startsWith(fieldInBreadcrumbSection)) {
                        return true
                    }
                }
            }

            return false
        },
        changeBreadcrumb (breadcrumb) {
            this.active_breadcrumb = breadcrumb
        },
        changeToPreviousBreadcrumb () {
            for (const i in this.breadcrumbs) {
                if (this.active_breadcrumb === this.breadcrumbs[i].key) {
                    this.changeBreadcrumb(this.breadcrumbs[Number(i) - 1].key)

                    return
                }
            }
        },
        changeToNextBreadcrumb () {
            for (const i in this.breadcrumbs) {
                if (this.active_breadcrumb === this.breadcrumbs[i].key) {
                    this.changeBreadcrumb(this.breadcrumbs[Number(i) + 1].key)

                    return
                }
            }
        },
        async getFFPackage () {
            this.ffPackageContracts = []
            let newFFPackage = []

            if (this.form.product_id) {
                this.isLoadingContractOptions = true

                newFFPackage = await window.axios.get(this.$route('customers.customers.contracts.ff-package', {
                    customer: this.customer.id,
                    product: this.form.product_id
                }))

                newFFPackage = newFFPackage.data.contracts
            }

            this.ffPackageContracts = newFFPackage
        },

        async getSupportedRelationTypes () {
            this.relationTypes = []
            if (this.form.parent_contract_id) {
                this.isLoadingContractOptions = true
                const relationTypes = await window.axios.get(this.$route('customers.customers.contracts.relation-types', {
                    customer: this.customer.id,
                    contract: this.form.parent_contract_id,
                    product: this.form.product_id
                }))
                this.relationTypes = relationTypes.data
                this.isLoadingContractOptions = false
            }
        },
        flattenContracts (contracts, parentKey = null) {
            if (contracts.length === 0) {
                return {}
            }

            let result = {}

            for (const contract of contracts) {
                const tmpContract = JSON.parse(JSON.stringify(contract))
                delete tmpContract.children

                tmpContract.parent_key = parentKey
                const configOptions = tmpContract.config_values
                tmpContract.config_values = {}

                for (const configValue of configOptions) {
                    tmpContract.config_values[configValue.name] = configValue
                }

                result[contract.key] = tmpContract
                result = { ...result, ...this.flattenContracts(contract.children, contract.key) }
            }

            return result
        },
        updatePromptCashDiscountPercentage (value) {
            this.form.prompt_cash_discount_percentage = value
        },
        updatePromptCashDiscountDays (value) {
            this.form.prompt_cash_discount_days = value
        },
        updatePaymentMethod (value) {
            this.form.payment_method = value
            if (!supportedPaymentMethods.includes(this.form.payment_method)) {
                this.form.installments = null
            }
        },
        updatePaymentTerm (value) {
            this.form.payment_term = value
        },
        updateInstallments (value) {
            if (value === '' || value === null) {
                this.form.installments = null
            } else {
                this.form.installments = value
                this.form.prompt_cash_discount_days = null
                this.form.prompt_cash_discount_percentage = null
                this.form.prepaid = false
            }
        },
        showPaymentBreadcrumbs () {
            return this.$page.props.tenantSettings?.enable_divergent_payment_methods === true ||
                this.$page.props.tenantSettings?.supports_installments === true ||
                (
                    (
                        this.isPromptCashDiscountEnabled === true ||
                        this.$page.props.tenantSettings?.enable_prompt_cash_discount === true
                    ) &&
                    supportedPaymentMethods.includes(this.customer.payment_method)
                )
        },
        setIncident (value) {
            this.form.incident_id = value
        },
        getPhaseTextFromProductPlan (productPlans, selectedPlan) {
            let phaseText = null

            productPlans.forEach(item => {
                if (item.value === parseInt(selectedPlan)) {
                    phaseText = item.label
                }
            })

            return phaseText
        },
        filterCurrentPhasePrice () {
            const today = getDateFormatted()
            const pricesDesc = [...this.form.prices].sort(
                (a, b) => b.start_date.localeCompare(a.start_date)
            )
            const current = pricesDesc.find(p => p.start_date <= today)
            // fallback: use the oldest price if all start dates are in the future.
            return current ?? pricesDesc.at(-1) ?? null
        },
        updatePreviousContractId (value) {
            this.form.previous_contract_ids = value
        },
    }
}
</script>

<style scoped lang="scss">
.contract-form {
    display: grid;
    grid-gap: 15px;
}

hr {
    width: 100%;
    margin-bottom: 31px !important;
}

.row-margin {
    margin-bottom: 25px !important;
}

.pricing-and-runtimes-padding {
    padding-bottom: 10px;
}

.basic-information-margin {
    margin-bottom: -16px !important;
}
</style>
